import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import dolibarrApi from '../services/dolibarrApi';

const stripePromise = loadStripe('pk_live_51SKlpvGe5rmpCcMtc2p47ScUJ3m3MITxaWQ888q0ikHWl5KwnIAmjj6YabpCvyQBqlFVGeCoJkEUAACmqhxggLUB00tGl5IVE5');
//const stripePromise = loadStripe('pk_test_51SKlpvGe5rmpCcMt6upHasCqjeXcQYlENdO6Je7OD2DpNPWkoAwkxZRG85JF7rEMEowgY5SIgTZSQ0Hc5UQqk3AE00J8VKR44N');


const CheckoutForm = ({ clientSecret, onTotalChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { items, getTotal, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [customerInfo, setCustomerInfo] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    address: '',
    zip: '',
    town: '',
    country_id: 1 // Default to UK
  });
  const [shippingInfo, setShippingInfo] = useState({
    method: '',
    cost: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Shipping calculation logic
  const isWestYorkshire = (postcode) => {
    if (!postcode) return false;
    const westYorkshirePrefixes = ['WF', 'BD', 'HX', 'LS', 'HD'];
    const prefix = postcode.trim().toUpperCase().substring(0, 2);
    return westYorkshirePrefixes.includes(prefix);
  };

  const isBeforeDeadline = () => {
    const deadline = new Date('2025-12-20T13:00:00'); // Dec 20, 1pm
    return new Date() < deadline;
  };

  const calculateShipping = () => {
    const postcode = customerInfo.zip.trim().toUpperCase();
    const isWY = isWestYorkshire(postcode);
    const beforeDeadline = isBeforeDeadline();

    if (isWY && beforeDeadline) {
      return { method: 'Free delivery (West Yorkshire)', cost: 0 };
    } else {
      return { method: 'UK Delivery', cost: 4.99 };
    }
  };

  // Update shipping when postcode changes
  useEffect(() => {
    if (customerInfo.zip) {
      const shipping = calculateShipping();
      setShippingInfo(shipping);
    }
  }, [customerInfo.zip]);

  // Update total when shipping changes
  useEffect(() => {
    const subtotal = getTotal();
    const total = subtotal + shippingInfo.cost;
    onTotalChange(total);
  }, [shippingInfo.cost, getTotal, onTotalChange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep1 = () => {
    const required = ['firstname', 'lastname', 'email', 'address', 'zip', 'town'];
    return required.every(field => customerInfo[field].trim() !== '');
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createDolibarrOrder = async () => {
    try {
      // First, check if customer already exists by searching for matching email
      let existingCustomer = null;
      try {
        const searchResults = await dolibarrApi.searchThirdParties({
          email: customerInfo.email
        });

        // Look for exact match on email, first name, and last name
        existingCustomer = searchResults.find(customer =>
          customer.email === customerInfo.email &&
          customer.name &&
          customer.name.toLowerCase().includes(customerInfo.firstname.toLowerCase()) &&
          customer.name.toLowerCase().includes(customerInfo.lastname.toLowerCase())
        );
      } catch (searchError) {
        console.log('No existing customer found, will create new one');
      }

      let thirdParty;
      if (existingCustomer) {
        // Use existing customer
        thirdParty = existingCustomer.id;
        console.log('Using existing customer:', existingCustomer.id);
      } else {
        // Create new third party (customer) - using the correct format based on existing third parties
        const thirdPartyData = {
          name: `${customerInfo.firstname} ${customerInfo.lastname}`,
          name_alias: customerInfo.email,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: customerInfo.address,
          zip: customerInfo.zip,
          town: customerInfo.town,
          country_id: customerInfo.country_id,
          client: "1", // Mark as customer (string format)
          fournisseur: "0", // Not a supplier
          status: "1", // Active status
          tva_assuj: "1", // Subject to VAT
          localtax1_assuj: "0",
          localtax2_assuj: "0",
          typent_id: "0",
          effectif_id: null,
          forme_juridique_code: null,
          capital: null,
          stcomm_id: "0",
          remise_percent: 0,
          remise_supplier_percent: "0",
          mode_reglement_supplier_id: null,
          cond_reglement_supplier_id: null,
          transport_mode_supplier_id: null,
          fk_prospectlevel: "",
          outstanding_limit: null,
          order_min_amount: null,
          supplier_order_min_amount: null,
          default_lang: null,
          ref: null,
          ref_ext: null,
          barcode_type: null,
          barcode_type_coder: null,
          webservices_url: null,
          webservices_key: null,
          logo: null,
          accountancy_code_sell: "",
          accountancy_code_buy: "",
          fk_incoterms: "0",
          label_incoterms: null,
          location_incoterms: null,
          socialnetworks: [],
          partnerships: [],
          SupplierCategories: [],
          // Use "auto" for automatic customer code generation as per Dolibarr docs
          code_client: "auto"
        };

        thirdParty = await dolibarrApi.createThirdParty(thirdPartyData);
        console.log('Created new customer:', thirdParty);
      }

      // Return third party ID only - no proposal creation needed
      return { thirdParty };
    } catch (error) {
      console.error('Error creating Dolibarr order:', error);
      throw new Error('Failed to create order. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Submit the payment form first (required by Stripe)
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    try {
      // Create order in Dolibarr first
      const { thirdParty } = await createDolibarrOrder();

      // Confirm payment with Stripe
      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/order-status`,
          payment_method_data: {
            billing_details: {
              name: `${customerInfo.firstname} ${customerInfo.lastname}`,
              email: customerInfo.email,
              address: {
                line1: customerInfo.address,
                postal_code: customerInfo.zip,
                city: customerInfo.town,
                country: 'GB'
              }
            }
          }
        },
        redirect: 'if_required'
      });

      if (result.error) {
        throw new Error(result.error.message);
      } else {
        // Payment succeeded - create invoice directly
        const invoiceData = {
          socid: thirdParty, // Third party ID
          date: Math.floor(Date.now() / 1000), // Current timestamp
          date_lim_reglement: Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60), // Payment due in 30 days
          type: 0, // Standard invoice
          remise_percent: 0,
          note_private: 'Invoice created via website payment',
          lines: items.map(item => ({
            fk_product: item.id,
            qty: item.quantity,
            subprice: item.price,
            tva_tx: 0.0, // 20% VAT
            remise_percent: 0,
            product_type: 0 // Product
          }))
        };

        const invoice = await dolibarrApi.createInvoice(invoiceData);
        console.log('Invoice created:', invoice);

        // Add payment to the invoice to mark it as paid
        const paymentData = {
          datepaye: Math.floor(Date.now() / 1000), // Current timestamp
          paymentid: 6, // Credit card payment method
          closepaidinvoices: "yes", // Close the invoice as paid
          accountid: 5, // Correct bank account for credit card payments
          num_payment: `STRIPE-${Date.now()}`, // Unique payment reference
          comment: 'Payment processed via Stripe'
        };

        await dolibarrApi.addInvoicePayment(invoice, paymentData);
        console.log('Payment added to invoice');

        // Send order details to webhook
        try {
          const orderData = {
            invoice_id: invoice,
            customer: {
              firstname: customerInfo.firstname,
              lastname: customerInfo.lastname,
              email: customerInfo.email,
              phone: customerInfo.phone,
              address: customerInfo.address,
              zip: customerInfo.zip,
              town: customerInfo.town,
              country_id: customerInfo.country_id
            },
            items: items.map(item => ({
              id: item.id,
              label: item.label,
              quantity: item.quantity,
              price: item.price,
              price_ttc: item.price_ttc,
              total: item.price_ttc * item.quantity
            })),
            shipping: {
              method: shippingInfo.method,
              cost: shippingInfo.cost
            },
            subtotal: getTotal(),
            total: getTotal() + shippingInfo.cost,
            currency: 'GBP',
            payment_method: 'stripe',
            timestamp: new Date().toISOString(),
            status: 'paid'
          };

          const webhookResponse = await fetch('https://firehousespirits.store/n8n/webhook/850bd7f5-0b55-40ff-b27a-7276a571653c', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Basic d2Vic3RvcmU6RURlOHJpSDU2WDF2YUliQjJxWjNBQ2cxTTNSZEgxYVMwN2VnaTRQM3hKSEM2NDdLYXA='
            },
            body: JSON.stringify(orderData)
          });

          if (!webhookResponse.ok) {
            console.warn('Webhook call failed:', webhookResponse.status, webhookResponse.statusText);
          } else {
            console.log('Order details sent to webhook successfully');
          }
        } catch (webhookError) {
          console.warn('Error sending to webhook:', webhookError);
          // Don't fail the order if webhook fails
        }

        // Create stock movements for sold items
        for (const item of items) {
          await dolibarrApi.createStockMovement({
            product_id: item.id,
            warehouse_id: 2, // Correct warehouse for stock decrease
            qty: -item.quantity, // Negative for stock reduction
            type: 2, // Output (stock decrease)
            movementcode: `INV${invoice}`,
            movementlabel: `Sale - Invoice ${invoice}`,
            price: item.price
          });
        }
        console.log('Stock movements created');

        // Stock movements are handled automatically by Dolibarr when invoice is validated
        // No need to manually create stock movements

        // Clear cart and redirect to success page
        clearCart();
        // Redirect to order tracking page
        window.location.href = `/order-status?ref=${invoice}`;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <section className="checkout">
        <h2>CHECKOUT</h2>
        <p>Your cart is empty. Please add some items before checking out.</p>
      </section>
    );
  }

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className="step-item">
        <div className={`step-circle ${currentStep >= 1 ? 'active' : ''}`}>
          {currentStep > 1 ? '✓' : '1'}
        </div>
        <span className="step-label">Customer Information</span>
      </div>
      <div className="step-connector"></div>
      <div className="step-item">
        <div className={`step-circle ${currentStep >= 2 ? 'active' : ''}`}>
          {currentStep > 2 ? '✓' : '2'}
        </div>
        <span className="step-label">Delivery</span>
      </div>
      <div className="step-connector"></div>
      <div className="step-item">
        <div className={`step-circle ${currentStep >= 3 ? 'active' : ''}`}>
          {currentStep > 3 ? '✓' : '3'}
        </div>
        <span className="step-label">Payment</span>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="checkout-step">
      <h3>Customer Information</h3>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstname">First Name *</label>
          <input
            type="text"
            id="firstname"
            name="firstname"
            value={customerInfo.firstname}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="lastname">Last Name *</label>
          <input
            type="text"
            id="lastname"
            name="lastname"
            value={customerInfo.lastname}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={customerInfo.email}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={customerInfo.phone}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="address">Address *</label>
        <input
          type="text"
          id="address"
          name="address"
          value={customerInfo.address}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="zip">Postcode *</label>
          <input
            type="text"
            id="zip"
            name="zip"
            value={customerInfo.zip}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="town">Town/City *</label>
          <input
            type="text"
            id="town"
            name="town"
            value={customerInfo.town}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <button
        type="button"
        className="btn checkout-next-btn"
        onClick={handleNextStep}
        disabled={!validateStep1()}
      >
        Continue to Delivery
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="checkout-step">
      <h3>Delivery Method</h3>

      <div className="shipping-options">
        <div className="shipping-option selected">
          <div className="shipping-details">
            <h4>{shippingInfo.method}</h4>
            <p className="shipping-cost">
              {shippingInfo.cost === 0 ? 'FREE' : `£${shippingInfo.cost.toFixed(2)}`}
            </p>
          </div>
          <div className="shipping-description">
            {shippingInfo.cost === 0
              ? 'Free delivery for West Yorkshire addresses (order before 20th December 1pm)'
              : 'Standard UK delivery'
            }
          </div>
        </div>
      </div>

      <div className="step-navigation">
        <button
          type="button"
          className="btn checkout-prev-btn"
          onClick={handlePrevStep}
        >
          Back
        </button>
        <button
          type="button"
          className="btn checkout-next-btn"
          onClick={handleNextStep}
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <form onSubmit={handleSubmit} className="checkout-step">
      <h3>Payment Information</h3>

      <div className="payment-section">
        <div className="stripe-card-element">
          {clientSecret && (
            <PaymentElement options={{ layout: { type: 'tabs', defaultCollapsed: false } }} />
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="step-navigation">
        <button
          type="button"
          className="btn checkout-prev-btn"
          onClick={handlePrevStep}
        >
          Back
        </button>
        <button
          type="submit"
          className="btn checkout-submit-btn"
          disabled={loading || !clientSecret}
        >
          {loading ? 'Processing...' : !clientSecret ? 'Loading payment...' : `Pay £${(getTotal() + shippingInfo.cost).toFixed(2)}`}
        </button>
      </div>
    </form>
  );

  return (
    <section className="checkout">
      <h2>CHECKOUT</h2>

      {renderStepIndicator()}

      <div className="checkout-content">
        <div className="order-summary">
          <h3>Order Summary</h3>
          {items.map((item) => (
            <div key={item.id} className="order-item">
              <span>{item.label} x {item.quantity}</span>
              <span>£{(item.price_ttc * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          {currentStep >= 2 && shippingInfo.cost > 0 && (
            <div className="order-item shipping-item">
              <span>{shippingInfo.method}</span>
              <span>£{shippingInfo.cost.toFixed(2)}</span>
            </div>
          )}
          {currentStep >= 2 && shippingInfo.cost === 0 && (
            <div className="order-item shipping-item free">
              <span>{shippingInfo.method}</span>
              <span>FREE</span>
            </div>
          )}
          <div className="order-total">
            <strong>
              Total: £{currentStep >= 2 ? (getTotal() + shippingInfo.cost).toFixed(2) : getTotal().toFixed(2)}
            </strong>
          </div>
        </div>

        <div className="checkout-form">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
      </div>
    </section>
  );
};

const Checkout = () => {
  const [clientSecret, setClientSecret] = useState('');
  const [finalTotal, setFinalTotal] = useState(0);
  const { items, getTotal } = useCart();
  const subtotal = getTotal();

  const handleTotalChange = (newTotal) => {
    setFinalTotal(newTotal);
  };

  // Fetch payment intent when subtotal or final total changes
  useEffect(() => {
    const totalToUse = finalTotal > 0 ? finalTotal : subtotal;
    if (items.length > 0 && totalToUse > 0) {
      const fetchPaymentIntent = async () => {
        try {
          const response = await fetch('/api/create-payment-intent', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: totalToUse,
              currency: 'gbp'
            }),
          });
          const paymentIntent = await response.json();
          if (paymentIntent.error) {
            console.error(`Payment creation failed: ${paymentIntent.error}`);
          } else {
            setClientSecret(paymentIntent.client_secret);
          }
        } catch (err) {
          console.error('Error fetching payment intent:', err.message);
        }
      };
      fetchPaymentIntent();
    }
  }, [items, subtotal, finalTotal]);

  // Only render Elements when there's something to pay for
  if (items.length === 0 || subtotal <= 0) {
    return (
      <section className="checkout">
        <h2>CHECKOUT</h2>
        <p>Your cart is empty. Please add some items before checking out.</p>
      </section>
    );
  }

  return (
    <Elements stripe={stripePromise} options={clientSecret ? { clientSecret, appearance: { theme: 'night' } } : { mode: 'payment', amount: Math.round((finalTotal > 0 ? finalTotal : subtotal) * 100), currency: 'gbp', appearance: { theme: 'night' } }}>
      <CheckoutForm clientSecret={clientSecret} onTotalChange={handleTotalChange} />
    </Elements>
  );
};

export default Checkout;
